import * as React from 'react';
import { WebAPI } from '../../MedicareProAPI/API/WebAPI';
import LoaderWheel from '../Components/LoaderWheel';
import { CmsFormSingle } from '../Components/CMSForm/CmsFormSingle';
import { AgentIndCmsFormModal } from '../Components/CMSForm/AgentIndCmsFormModal';
import { UserSoaRepository } from '../../MedicareProAPI/Repositories/User/UserSoaRepository';
import { UserSoaStatusRepository } from '../../MedicareProAPI/Repositories/User/UserSoaStatusRepository';
import type { UserSoa } from '../../MedicareProAPI/Entities/User/UserSoa';
import type { UserSoaStatus } from '../../MedicareProAPI/Entities/User/UserSoaStatus';
import { IndDocumentRepository } from '../../MedicareProAPI/Repositories/Ind/IndDocumentRepository';
import type { Ind } from '../../MedicareProAPI/Entities/Ind/Ind';
import type { User } from '../../MedicareProAPI/Entities/User/User';
import type { IndDocument } from '../../MedicareProAPI/Entities/Ind/IndDocument';
import StyledModal from '../Components/StyledModal';
import type { INTEGRATION_TYPE } from '../Constants/Integrations';
import { INTEGRATION_OPTIONS } from '../Constants/Integrations';
import { IntegrationManager } from '../Components/IntegrationManager';

export interface AgentIndProfileCmsFormsPanelProps {
  userId: number;
  userAccessToken: string;
  indId: number;
  agencyId?: number;
  usersAgencyId?: number;
  directUplineId?: number;
  absoluteUplineId?: number;
  framed?: boolean;
  title?: string;
  className?: string;
}

/** Content-only props (what the inner content needs) */
type SoaConsentContentProps = {
  startingNewForm: boolean;
  formTypeSelected: string | null;
  handleFormModalClose: () => void;
  handleSaveNewForm: (indId: number, formData: any) => Promise<UserSoa>;
  handleEmailSend: (data: any, userSoa: UserSoa, individual: Ind, agent: User) => Promise<string>;
  handleSmsSend: (data: any, soa: UserSoa, agent: User) => Promise<string>;
  handleFormPrintF2f: (soa: UserSoa) => Promise<string>;
  handleFormStatusSave: (message: string, soaId: number) => Promise<UserSoaStatus>;
  handleDocumentSave: (indId: number, fileData: any) => Promise<IndDocument | null>;
  handleFormTypeClick: (type: string) => void;
  handleFormsRefresh: () => void;
  isFetchingForms: boolean;
  cmsForms: UserSoa[];
  handleFormDelete: (indId: number, soaId: number) => void;
  handleEmailSendCopy: (
    data: any,
    userSoa: UserSoa,
    individual: Ind,
    agent: User
  ) => Promise<string>;
  handleSmsSendCopy: (data: any, soa: UserSoa, agent: User) => Promise<string>;
  handleUpdateCode: (indId: number, soaId: number, payload: any) => Promise<UserSoa>;
  handleFormUpdate: (indId: number, soaId: number, payload: any) => Promise<UserSoa>;
  agencyId: number;
  props: any; // passthrough if you truly need it
};

const SoaConsentContent: React.FC<SoaConsentContentProps> = (p) => (
  <>
    {p.startingNewForm && (
      <AgentIndCmsFormModal
        formTypeSelected={p.formTypeSelected!}
        props={p.props}
        onClose={p.handleFormModalClose}
        onSave={p.handleSaveNewForm}
        onEmailSend={p.handleEmailSend}
        onSmsSend={p.handleSmsSend}
        onFormPrintF2f={p.handleFormPrintF2f}
        onSaveFormStatus={p.handleFormStatusSave}
        onDocUpload={p.handleDocumentSave}
        agencyId={p.agencyId}
      />
    )}

    {/* Actions */}
    <div className="row gutter-2 mb-2 p-2">
      <div className="col-12">
        <div
          className="btn btn-sm border border-primary text-primary hover-shadow p-1 d-inline-block cp px-2 mr-2"
          style={{ border: 'none', borderRadius: '15px' }}
          onClick={() => p.handleFormTypeClick('soa')}
        >
          <i className="fas fa-plus-circle mr-1" /> New SOA
        </div>
        <div
          className="btn btn-sm border border-primary text-primary hover-shadow p-1 d-inline-block cp px-2 mr-2"
          style={{ border: 'none', borderRadius: '15px' }}
          onClick={() => p.handleFormTypeClick('consentA')}
        >
          <i className="fas fa-plus-circle mr-1" /> New ACA Consent (A)
        </div>
        <div
          className="btn btn-sm border border-primary text-primary hover-shadow p-1 d-inline-block cp px-2 mr-2"
          style={{ border: 'none', borderRadius: '15px' }}
          onClick={() => p.handleFormTypeClick('consentB')}
        >
          <i className="fas fa-plus-circle mr-1" /> New ACA Consent (B)
        </div>
        <span
          className="badge badge-secondary mr-2"
          onClick={() => p.handleFormTypeClick('manual')}
        >
          Add manually
        </span>
        <span>
          <i
            className="far fa-sync-alt text-info cp"
            onClick={p.handleFormsRefresh}
            data-toggle="tooltip"
            title="refresh"
          />
        </span>
      </div>
    </div>

    {/* List */}
    <div className="row gutter-2 mb-2 p-2">
      {p.isFetchingForms && <LoaderWheel />}

      {!p.isFetchingForms &&
        p.cmsForms.length > 0 &&
        p.cmsForms.map((cmsForm: UserSoa) => (
          <CmsFormSingle
            key={cmsForm.getSoaId()}
            props={p.props}
            formData={cmsForm}
            handleFormDelete={p.handleFormDelete}
            onEmailSend={p.handleEmailSend}
            onSmsSend={p.handleSmsSend}
            onEmailSendCopy={p.handleEmailSendCopy}
            onSmsSendCopy={p.handleSmsSendCopy}
            onFormPrintF2f={p.handleFormPrintF2f}
            onUpdateCode={p.handleUpdateCode}
            onFormUpdate={p.handleFormUpdate}
            onSaveFormStatus={p.handleFormStatusSave}
          />
        ))}

      {!p.isFetchingForms && p.cmsForms.length === 0 && (
        <div className="ml-4">No forms to show</div>
      )}
    </div>
  </>
);

/**
 * Panel for managing CRUD operations for CMS forms
 */
export const AgentIndProfileCmsFormsPanel: React.FC<AgentIndProfileCmsFormsPanelProps> = (
  props
) => {
  const {
    userId,
    userAccessToken,
    indId,
    agencyId = 0,
    framed = true,
    title = "SOA's/Consents",
    className = 'card cardAddress',
  } = props;

  const webAPI = new WebAPI();
  webAPI.setAccessToken(userAccessToken);

  const userSoaRepository = new UserSoaRepository();
  userSoaRepository.setAccessToken(userAccessToken);

  const userSoaStatusRepository = new UserSoaStatusRepository();
  userSoaStatusRepository.setAccessToken(userAccessToken);

  const indDocumentRepository = new IndDocumentRepository();
  indDocumentRepository.setAccessToken(userAccessToken);

  const [startingNewForm, setStartingNewForm] = React.useState<boolean>(false);
  const [isFetchingForms, setIsFetchingForms] = React.useState<boolean>(true);
  const [formTypeSelected, setFormTypeSelected] = React.useState<string | null>(null);
  const [cmsForms, setCmsForms] = React.useState<UserSoa[]>([]); // init array
  const [show, setShow] = React.useState<boolean>(false);

  const [showIntegrationSetup, setShowIntegrationSetUp] = React.useState<boolean>(false);
  const [allowedIntegrationServices, setAllowedIntegrationServices] = React.useState<Array<any>>(
    []
  );

  const typeLegend = React.useMemo(
    () => [
      {
        language: 'ENG',
        value: 'soa',
        label: 'SOA',
        emailTemplateName: 'ClientSoaSend',
        subject: 'Scope of Appointment',
      },
      {
        language: 'ENG',
        value: 'consentA',
        label: 'Consent A',
        emailTemplateName: 'ClientConsentASend',
        subject: 'Consumer Consent Form A',
      },
      {
        language: 'ENG',
        value: 'consentB',
        label: 'Consent B',
        emailTemplateName: 'ClientConsentBSend',
        subject: 'Consumer Consent Form B',
      },
      {
        language: 'ESP',
        value: 'soa',
        label: 'SOA',
        emailTemplateName: 'ClientSoaSend_ESP',
        subject: 'Alcance del Formulario de Confirmación de Cita',
      },
      {
        language: 'ESP',
        value: 'consentA',
        label: 'Consent A',
        emailTemplateName: 'ClientConsentASend_ESP',
        subject: 'Consumer Consent Form A',
      },
      {
        language: 'ESP',
        value: 'consentB',
        label: 'Consent B',
        emailTemplateName: 'ClientConsentBSend_ESP',
        subject: 'Consumer Consent Form B',
      },
      {
        language: '',
        value: 'manual',
        label: 'Manual Document',
        emailTemplateName: '',
        subject: '',
      },
    ],
    []
  );

  const handleSetUpIntegrationHide = () => {
    setShowIntegrationSetUp(false);
  };

  const handleIntegrationSetup = (serviceType: INTEGRATION_TYPE) => {
    setShow(false);
    setAllowedIntegrationServices([...INTEGRATION_OPTIONS[serviceType]]);
    setShowIntegrationSetUp(true);
  };

  /**
   * Handles clicking whuch form to start making
   */
  const handleFormTypeClick = (formType: string) => {
    setFormTypeSelected(formType);
    setStartingNewForm(true);
  };

  const handleFormModalClose = () => {
    setFormTypeSelected(null);
    setStartingNewForm(false);
  };

  const handleFormDelete = (indIdParam: number, formId: number) => {
    userSoaRepository.removeIndSoa(indIdParam, formId).then(() => {
      setCmsForms((prev) => prev.filter((item) => item.getSoaId() !== formId));
    });
  };

  const handleSaveNewForm = (indIdParam: number, formData: any) => {
    return userSoaRepository.createIndSoa(indIdParam, formData).then((newForm) => {
      setCmsForms((prev) => [newForm, ...prev]);
      return newForm;
    });
  };

  const handleFormStatusSave = (message: string, soaId: number) => {
    const statusPayload = { soaStatus: message, soaId };
    return userSoaStatusRepository.createIndSoaStatus(indId, statusPayload);
  };

  const handleEmailSend = (
    sendingData: any,
    userSoa: UserSoa,
    individual: Ind,
    selectedAgent: User
  ) => {
    const sendTypeObject = typeLegend.filter(
      (item) => item.language === userSoa.getSoaLanguage() && item.value === userSoa.getSoaValue()
    );
    const subject = sendTypeObject[0]?.subject ?? '';
    const emailTemplateName = sendTypeObject[0]?.emailTemplateName ?? '';

    let signature = '';
    let ignore = '';
    if ((selectedAgent as any)['signatureEmail']) {
      signature = (selectedAgent as any)['signatureEmail']['signature'];
      ignore = 'ignoreHide';
    }
    if (signature === '') {
      signature =
        `<div>${selectedAgent.getUserFirstname()} ${selectedAgent.getUserLastname()}</div>` +
        `<div>${selectedAgent.getUserUsername()}</div>` +
        `<div>${selectedAgent.getUserPhone()}</div>`;
    }

    const codeLine = userSoa.isSoaNoAuth()
      ? ''
      : `You will also need your ACCESS CODE: ${userSoa.getCode()}`;
    const payloadValues = {
      clientName: `${individual.getIndFirstname()},`,
      links: userSoa.getUrlClient(),
      signature,
      ignore,
      code: codeLine,
    };

    const sendingResponse = !sendingData.provider
      ? webAPI.sendEmail({
          to: sendingData.to,
          from: selectedAgent.getUserUsername(),
          subject,
          templateName: emailTemplateName,
          templateValues: payloadValues,
        })
      : webAPI.newUserIntegrationMail(sendingData.provider.reference, null, {
          subject,
          message: 'systemTemplate',
          to: [sendingData.to],
          cc: null,
          systemTemplate: {
            templateType: 'email',
            templateName: emailTemplateName,
            templateValues: payloadValues,
          },
        });

    return sendingResponse.then((r: any) => r).catch((e: any) => e);
  };

  const handleSmsSend = (sendingData: any, userSoa: UserSoa, selectedAgent: User) => {
    const baseEng = `This is ${selectedAgent.getUserFirstname()}, Please use the link below to view and sign your form. ${userSoa.getUrlClient()}`;
    const baseEsp = `Este es ${selectedAgent.getUserFirstname()}, Utilice el enlace a continuación para ver y firmar el Alcance del Formulario de Confirmación de Cita. ${userSoa.getUrlClient()}`;
    let smsMessage = userSoa.getSoaLanguage() === 'ESP' ? baseEsp : baseEng;
    if (!userSoa.isSoaNoAuth()) smsMessage += ` Here is your access code: ${userSoa.getCode()}`;

    const sendingResponse =
      typeof sendingData.provider === 'object' && sendingData.provider !== null
        ? webAPI.sendUserVoipSms(
            sendingData.provider.reference,
            {
              to: sendingData.to,
              from: sendingData.from,
              message: smsMessage,
              accountId: sendingData.provider.account_id,
              extensionId: sendingData.extension,
            },
            sendingData.provider.entityType
          )
        : webAPI.sendSMS({ to: sendingData.to, message: smsMessage });

    return sendingResponse.then((r: any) => r).catch((e: any) => e);
  };

  const handleEmailSendCopy = (
    sendingData: any,
    userSoa: UserSoa,
    individual: Ind,
    selectedAgent: User
  ) => {
    let signature = '';
    let ignore = '';
    if ((selectedAgent as any)['signatureEmail']) {
      signature = (selectedAgent as any)['signatureEmail']['signature'];
      ignore = 'ignoreHide';
    }
    if (signature === '') {
      signature =
        `<div>${selectedAgent.getUserFirstname()} ${selectedAgent.getUserLastname()}</div>` +
        `<div>${selectedAgent.getUserUsername()}</div>` +
        `<div>${selectedAgent.getUserPhone()}</div>`;
    }

    const subject = 'Access your completed document';
    const templateName = 'ClientFormSendCopy';
    const templateValues = {
      clientName: `${individual.getIndFirstname()},`,
      links: `${userSoa.getUrlClient()}/copy`,
      signature,
      ignore,
      code: userSoa.getCode(),
    };

    const sendingResponse = !sendingData.provider
      ? webAPI.sendEmail({
          to: sendingData.to,
          from: selectedAgent.getUserUsername(),
          subject,
          templateName,
          templateValues,
        })
      : webAPI.newUserIntegrationMail(sendingData.provider.reference, null, {
          subject,
          message: 'systemTemplate',
          to: [sendingData.to],
          cc: null,
          systemTemplate: { templateType: 'email', templateName, templateValues },
        });

    return sendingResponse.then((r: any) => r).catch((e: any) => e);
  };

  const handleSmsSendCopy = (sendingData: any, userSoa: UserSoa, selectedAgent: User) => {
    const baseEng = `This is ${selectedAgent.getUserFirstname()}, Please use the link below to view your completed document. ${userSoa.getUrlClient()}`;
    const baseEsp = `Este es ${selectedAgent.getUserFirstname()}, Utilice el enlace a continuación para ver su documento completo. ${userSoa.getUrlClient()}`;
    const smsMessage = userSoa.getSoaLanguage() === 'ESP' ? baseEsp : baseEng;

    const sendingResponse =
      typeof sendingData.provider === 'object'
        ? webAPI.sendUserVoipSms(
            sendingData.provider.reference,
            {
              to: sendingData.to,
              from: sendingData.from,
              message: smsMessage,
              accountId: sendingData.provider.account_id,
              extensionId: sendingData.extension,
            },
            sendingData.provider.entityType
          )
        : webAPI.sendSMS({ to: sendingData.to, message: smsMessage });

    return sendingResponse.then((r: any) => r).catch((e: any) => e);
  };

  const handleFormPrintF2f = (userSoa: UserSoa) =>
    new Promise<string>((resolve) => {
      window.open(userSoa.getUrlAgent());
      resolve('form opened');
    });

  const handleUpdateCode = (indIdParam: number, formId: number, payload: any) =>
    userSoaRepository.updateIndSoaCode(indIdParam, formId, payload);

  const handleFormsRefresh = () => {
    setIsFetchingForms(true);
    fetchForms();
  };

  const fetchForms = () => {
    userSoaRepository.getIndSoas(indId).then((response) => {
      setCmsForms(response.getItems());
      setIsFetchingForms(false);
    });
  };

  const handleFormUpdate = (indIdParam: number, formId: number, payload: any) =>
    userSoaRepository.updateIndSoa(indIdParam, formId, payload);

  const handleDocumentSave = async (indIdParam: number, attachment: any) => {
    try {
      const payload = { indDocName: attachment.fileName, indDocSystemName: attachment.name };
      const response = await indDocumentRepository.createIndDocument(indIdParam, payload);
      return response;
    } catch (error) {
      console.error('Document save failed:', error);
      return null;
    }
  };

  React.useEffect(() => {
    fetchForms();
  }, [indId, userAccessToken]);

  // Build the content props once per render
  const contentProps: SoaConsentContentProps = {
    startingNewForm,
    formTypeSelected,
    handleFormModalClose,
    handleSaveNewForm,
    handleEmailSend,
    handleSmsSend,
    handleFormPrintF2f,
    handleFormStatusSave,
    handleDocumentSave,
    handleFormTypeClick,
    handleFormsRefresh,
    isFetchingForms,
    cmsForms,
    handleFormDelete,
    handleEmailSendCopy,
    handleSmsSendCopy,
    handleUpdateCode,
    handleFormUpdate,
    agencyId,
    props, // pass-through if needed by child
  };

  if (!framed) {
    return <SoaConsentContent {...contentProps} />;
  }

  return (
    <>
      {showIntegrationSetup && (
        <IntegrationManager
          userAccessToken={props.userAccessToken}
          userId={props.userId}
          showIntegrationSetup={showIntegrationSetup}
          onHide={handleSetUpIntegrationHide}
          allowedServices={allowedIntegrationServices}
        />
      )}
      <div className={className}>
        <div className="card-header">{title}</div>
        <div className="card-body">
          <SoaConsentContent {...contentProps} />
        </div>
      </div>
    </>
  );
};
